<script setup lang="ts">
import {Appointment} from "@/core/interfaces/Appointment";
import {computed} from "vue";
import {Clock} from "@lucide/vue";
import {SupportProgramIndexResource} from "@/core/http/resources/SupportProgramResource";
import TagsIcon from "@/tag/components/TagsIcon.vue";

const props = defineProps<{
    appointment: Appointment;
}>();

defineEmits<{
    /** The card was clicked: the timeline opens the appointment's detail. */
    (e: 'select'): void;
}>();

const formattedTime = computed(() => {
    return new Date(props.appointment.start_at).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
    });
});

/**
 * Type guard to check if the appointment is for a support program.
 */
const isSupportAppointment = (appointment: Appointment): appointment is Appointment & {
    appointable: SupportProgramIndexResource
} => {
    return appointment.appointmentable_type === 'support';
};
</script>

<template>
  <button
    type="button"
    class="w-full text-left rounded-lg px-4 py-2 transition-all duration-200 cursor-pointer group bg-gray-50 border border-gray-100"
    @click="$emit('select')"
  >
    <div class="flex items-start gap-3">
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2 justify-between">
          <p
            class="font-semibold text-sm text-gray-900 group-hover:text-primary transition-colors leading-tight line-clamp-2"
          >
            <TagsIcon
              v-if="isSupportAppointment(appointment)"
              :tags="appointment.appointable.tags"
              class="inline-flex w-5 h-5"
            />
            {{ appointment.title }}
          </p>

          <div class="flex items-center gap-1 text-xs text-gray-600">
            <Clock class="w-3 h-3 text-gray-500" />
            <span class="font-medium leading-none">{{ formattedTime }}</span>
          </div>
        </div>

        <p v-if="appointment.pharmacist_memo" class="text-primary text-sm mt-1">
          {{ appointment.pharmacist_memo }}
        </p>
      </div>
    </div>
  </button>
</template>

<style scoped>
.group {
    transform: translateY(0);
    transition: transform 0.2s ease-in-out;
}

.group:hover {
    transform: translateY(-2px);
}
</style>
